import { Link } from "react-router-dom";
import { ArrowRight, Bell, CalendarDays, MapPin, Users, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import { MINISTRIES } from "@/data/ministries";
import { trackPlanVisitClick } from "@/lib/analytics";
import LaunchTextCapture from "@/components/LaunchTextCapture";

export default function MinistryPage({ slug }) {
  const m = MINISTRIES[slug];
  if (!m) return null;

  const tiles = [
    { icon: CalendarDays, label: "When", value: m.when },
    { icon: Users, label: "Who It's For", value: m.who },
    { icon: MapPin, label: "Where", value: m.where },
  ];

  return (
    <>
      <SEO title={m.name} description={m.meta} path={`/${slug}`} />

      <section className="grain relative overflow-hidden bg-navy pb-16 pt-14 text-white sm:pb-20 sm:pt-20" data-testid="ministry-hero">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-navy-light/50 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <nav className="text-xs uppercase tracking-[0.2em] text-white/50" aria-label="Breadcrumb">
              <Link to="/" className="transition-colors hover:text-white">Home</Link>
              <span className="mx-2" aria-hidden="true">/</span>
              <span className="text-white/80">{m.name}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 inline-block rounded-full bg-victory px-4 py-1.5 font-display text-[11px] font-700 uppercase tracking-[0.25em] text-white" data-testid="ministry-eyebrow">
              {m.eyebrow}
            </p>
          </Reveal>
          <LineReveal
            className="mt-4 font-display text-4xl font-900 leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl"
            lines={[m.headline]}
            delay={0.15}
          />
          <Reveal delay={0.3}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">{m.intro}</p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:gap-4">
              <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="ministry-cta-plan"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-victory px-8 py-4 font-display text-base font-700 tracking-wide text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark hover:shadow-lift">
                PLAN YOUR VISIT <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <a href="#launch-text" data-testid="ministry-cta-notify"
                 className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-8 py-4 font-display text-base font-700 tracking-wide text-white transition-colors duration-300 hover:border-white hover:bg-white/10">
                <Bell size={16} aria-hidden="true" /> GET LAUNCH UPDATES
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mx-auto mt-12 max-w-4xl">
              <img src={m.artwork} alt={m.artworkAlt} width="1672" height="941" loading="lazy"
                   className="aspect-[16/9] w-full rounded-3xl object-cover shadow-lift" data-testid="ministry-artwork" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-14 sm:py-20" data-testid="ministry-glance">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Stagger className="grid gap-5 sm:grid-cols-3 sm:gap-6">
            {tiles.map((t) => (
              <StaggerItem key={t.label}>
                <div className="h-full rounded-3xl border border-navy/10 bg-white p-7 shadow-soft" data-testid={`ministry-tile-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-navy text-white">
                    <t.icon size={20} aria-hidden="true" />
                  </span>
                  <p className="mt-4 font-display text-xs font-700 uppercase tracking-[0.25em] text-slate-400">{t.label}</p>
                  <p className="mt-1.5 font-display text-lg font-800 tracking-tight text-navy">{t.value}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24" data-testid="ministry-points">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <p className="text-center font-display text-sm font-700 uppercase tracking-[0.3em] text-victory">What to Expect</p>
            <h2 className="mt-3 text-balance text-center font-display text-3xl font-900 tracking-tighter text-navy sm:text-5xl">
              {m.name.toUpperCase()}.
            </h2>
          </Reveal>
          <Stagger className="mt-10 grid gap-4 sm:grid-cols-2">
            {m.points.map((p) => (
              <StaggerItem key={p}>
                <div className="flex h-full items-center gap-3 rounded-2xl border border-navy/10 bg-cream px-5 py-4">
                  <CheckCircle2 size={20} className="shrink-0 text-victory" aria-hidden="true" />
                  <span className="text-sm font-medium leading-relaxed text-navy">{p}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.15}>
            <p className="mt-10 text-center font-serif text-xl italic text-slate-600 sm:text-2xl">{m.closing}</p>
          </Reveal>
        </div>
      </section>

      <div id="launch-text">
        <LaunchTextCapture page={`/${slug}`} />
      </div>
    </>
  );
}
